import React, { useContext, useEffect, useRef, useState, lazy } from 'react';
import LinearProgress from '@material-ui/core/LinearProgress';
import {
  APIAlert,
  FajitaFormHeader,
  FajitaSignature,
} from '@mummssoftware/common-ui';
import {
  GlobalContext,
  FormsGlobalContext,
} from '@mummssoftware/global-context';
import {
  ANSWERED,
  ALL,
  STRING_TRUE,
  Submitted,
} from '@mummssoftware/utils/constants';
import { checkTrue, checkVisitAttribute } from '@mummssoftware/utils/utils';
import {
  isHopeNvrOrInaFormKey,
  isHopeRequiredToggleLocked,
  dedupeHopeAddendumProgressNotes,
} from '@mummssoftware/utils/hope';
import { getVisitByPersonAndVisitId } from '@mummssoftware/utils/pimReader';
import { isF2Fform } from '@mummssoftware/utils/form-engine/form-engine-utils';

import { ENTITY_MANIFEST } from '@mummssoftware/global-context/PNCounter/constants';
import {
  bucketFetch,
  hasArchive,
  hasFormSubmission,
} from '@mummssoftware/utils/form-engine/form-engine-archives';
import {
  SHOW_REQ_SWITCH,
  SET_FAJITA_SELECTED,
  SHOW_STICKIES,
  SHOW_READ_ONLY_FORM_SWITCH,
  SET_REQ_QUEST,
} from './Context/constants';
import { FajitaContext } from './Context';
import { FormRenderer, ReadOnlyForm } from '../FormEngine/Engine';
import { Context } from '../../context';
import { FormDiv, FajitaHeaderDiv } from '../../components/layout/WrapperDiv';

import defaults from '../../translations/en.json';

const FormEngineContextProvider = lazy(() =>
  import('../FormEngine/FormEngineContext/FormEngineContext'),
);

export const FajitaForms = () => {
  const { fajitaQuestionnaireFormGlobal } = useContext(FormsGlobalContext);
  const { appVisit } = useContext(GlobalContext);

  const {
    agency,
    patient,
    errorInfo,
    clearError,
    observation,
    keycloak,
    siteId,
  } = useContext(Context);
  const { parsingFormError, dispatch } = useContext(FajitaContext);

  /* eslint-disable camelcase */
  const form_id = fajitaQuestionnaireFormGlobal?.form_id;

  const [fajitaForm, setFajitaForm] = useState<mumms.FormEntity | null>(null);
  const [gpiFajitaForm, setGPIFajitaForm] = useState<mumms.FormEntity | null>(
    null,
  );
  const [savedForm, setSavedForm] = useState<mumms.FormEntity | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [bucketMode, setBucketMode] = useState<boolean>(false);
  const [gApi, setgApi] = useState<string | null>(null);
  const [formNotes, setFormNotes] = useState<any>(null);
  // Only clear the read/print toggle when the form identity changes, not on
  // Read↔Print reloads of the same manifest (avoids momentary hide).
  const loadedManifestIdRef = useRef<number | null | undefined>(undefined);

  useEffect(() => {
    dispatch({
      type: SET_FAJITA_SELECTED,
      payload: true,
    });
    if (fajitaQuestionnaireFormGlobal) {
      getFormData();
    } else {
      loadedManifestIdRef.current = undefined;
    }
  }, [fajitaQuestionnaireFormGlobal]);

  useEffect(() => {
    if (parsingFormError && !bucketMode) {
      getFajitaFormById(form_id, patient?.patientNumber, agency as string);
    }
  }, [parsingFormError]);

  const getFormData = async () => {
    const isInProgressFAJITA =
      fajitaQuestionnaireFormGlobal?.isInProgressFAJITA;
    const isPrintViewFAJITA = fajitaQuestionnaireFormGlobal?.isPrintViewFAJITA;
    const isReadOnlyViewFAJITA =
      fajitaQuestionnaireFormGlobal?.isReadOnlyViewFAJITA;
    const patientmanifest_id =
      fajitaQuestionnaireFormGlobal?.patientmanifest_id;
    // OP-32965: hide until API proves a real submission — but only reset when
    // opening a different form, so view-mode toggles do not flash the switch off.
    if (loadedManifestIdRef.current !== patientmanifest_id) {
      dispatch({
        type: SHOW_READ_ONLY_FORM_SWITCH,
        payload: false,
      });
      loadedManifestIdRef.current = patientmanifest_id;
    }
    if (isPrintViewFAJITA) {
      getFajitaFormSubmission(patientmanifest_id as number);
    }
    // case 2: in progress
    else if (isInProgressFAJITA || isReadOnlyViewFAJITA) {
      getInProgressFajitaFormById(patientmanifest_id, agency as string);
    } else {
      // case 3 : new form
      // not used in QUESTIONNAIRES widget = only show in progress or completed forms
      getFajitaFormById(form_id, patient?.patientNumber, agency as string);
    }
  };
  // set the read only form switch
  const setReadOnlyFormSwitch = (res: any) => {
    dispatch({
      type: SHOW_READ_ONLY_FORM_SWITCH,
      payload: !!hasFormSubmission(res),
    });
  };

  // display or not the required questions switch
  const checkSwitch = async (res: any) => {
    const isInProgressFAJITA =
      fajitaQuestionnaireFormGlobal?.isInProgressFAJITA;
    if (isInProgressFAJITA) {
      const nonRequiredAllowed = res.formisnonrequiredallowed;

      if (appVisit) {
        // For INA and NVR Forms
        // if true you CANNOT show the switch
        const checkReq = checkVisitAttribute(res, appVisit);
        dispatch({
          type: SHOW_REQ_SWITCH,
          payload: checkReq ? false : checkTrue(nonRequiredAllowed),
        });
      } else if (
        isHopeNvrOrInaFormKey(res.formkey) &&
        res.status === Submitted &&
        res.patientvisit_id
      ) {
        // Editing a completed HOPE form without an active visit: look up the visit
        // the manifest was submitted against to determine whether required questions
        // were enforced then (HAD for INA, HUV1/HUV2 for NVR).
        try {
          const visitRes = await getVisitByPersonAndVisitId(
            res.createpersonnumber || '',
            String(res.patientvisit_id),
            agency as string,
            keycloak!,
            res.patientnumber,
          );
          const visit = Array.isArray(visitRes) ? visitRes[0] : visitRes;
          const locked = isHopeRequiredToggleLocked(
            res.formname || res.displayname || '',
            visit?.visitattrsumdto,
          );
          dispatch({
            type: SHOW_REQ_SWITCH,
            payload: locked ? false : checkTrue(nonRequiredAllowed),
          });
        } catch {
          // Conservative: hide toggle for HOPE forms when visit fetch fails
          dispatch({ type: SHOW_REQ_SWITCH, payload: false });
        }
      } else {
        // @note what are the forms that do not require a visit
        dispatch({
          type: SHOW_REQ_SWITCH,
          payload: checkTrue(nonRequiredAllowed),
        });
      }
    } else {
      dispatch({ type: SHOW_REQ_SWITCH, payload: false });
      dispatch({ type: SET_REQ_QUEST, payload: false });
    }
  };
  const getNotes = async (id?: number) => {
    const pnNotes =
      observation &&
      patient &&
      (await observation.getWidgetPnNotes(
        patient?.patientNumber as string,
        agency,
        ENTITY_MANIFEST,
      ));

    if (pnNotes && Object.keys(pnNotes) && id) {
      const notes = pnNotes[id];

      notes && setFormNotes(dedupeHopeAddendumProgressNotes(notes));
    }
  };

  // NOT USED
  const getFajitaFormById = async (
    id?: number, // form_id
    patientnumber?: string,
    form_agency?: string,
  ) => {
    await observation
      .getFajitaFormById(id, patientnumber, form_agency)
      .then(async (res: any) => {
        if (!(res instanceof Response)) {
          setFormError(null);

          // required form switch
          dispatch({
            type: SHOW_REQ_SWITCH,
            payload: checkTrue(res.isnonrequiredallowed),
          });
          setFajitaForm(res);
        } else {
          setFormError(defaults.errorForm);
        }
      })
      .catch(() => {
        setFormError(defaults.errorForm);
      });
  };

  useEffect(() => {
    if (gApi && gpiFajitaForm) {
      const isPrintViewFAJITA =
        fajitaQuestionnaireFormGlobal?.isPrintViewFAJITA;
      const isReadOnlyViewFAJITA =
        fajitaQuestionnaireFormGlobal?.isReadOnlyViewFAJITA;

      const formRes = { ...gpiFajitaForm, formsubmission: gApi };

      if (isPrintViewFAJITA) {
        setSavedForm(formRes);
        setFormError(null);
      } else {
        const gFormRes = isReadOnlyViewFAJITA
          ? { ...formRes, readonly: true }
          : formRes;
        setFajitaForm(gFormRes);
      }
      setReadOnlyFormSwitch(formRes);

      setgApi(null);
    }
  }, [gApi]);

  const getArchiveUrl = async (res: any) => {
    setBucketMode(true);

    await bucketFetch(res, keycloak, setgApi, setFormError);
    setGPIFajitaForm(res);
  };

  const getInProgressFajitaFormById = async (
    id?: number, // patientmanifest_id
    form_agency?: string,
    readonly?: boolean,
  ) => {
    const submode = ALL;
    setSavedForm(null);
    await getNotes(id);

    await observation
      .getFajitaFormByIdByMode(id, submode, form_agency)
      .then(async (res: any) => {
        if (!(res instanceof Response)) {
          setFormError(null);
          await checkSwitch(res);
          setReadOnlyFormSwitch(res);

          // if archives document fetch it
          if (hasArchive(res)) {
            getArchiveUrl(res);
          } else {
            const isReadOnlyViewFAJITA =
              fajitaQuestionnaireFormGlobal?.isReadOnlyViewFAJITA;
            const isInProgressFAJITA =
              fajitaQuestionnaireFormGlobal?.isInProgressFAJITA;
            // Only prompt sticky prefill for true WIP forms, not completed edits
            if (isInProgressFAJITA && res.sticky && res.status !== Submitted) {
              dispatch({
                type: SHOW_STICKIES,
                payload: STRING_TRUE,
              });
            }
            const form =
              isReadOnlyViewFAJITA || readonly
                ? { ...res, readonly: true }
                : res;
            setFajitaForm(form);
          }
        } else {
          setFormError(defaults.errorForm);
        }
      })
      .catch(() => {
        setFormError(defaults.errorForm);
      });
  };

  const getFajitaFormSubmission = async (id: number) => {
    const mode: mumms.CPPMode = ANSWERED;
    await getNotes(id);
    await observation
      .getFajitaFormWithExtraData(id, mode, agency as string, siteId as string)
      .then(async (res: any) => {
        if (!(res instanceof Response)) {
          // if archives document fetch it
          if (hasArchive(res)) {
            getArchiveUrl(res);
          } else if (hasFormSubmission(res)) {
            setSavedForm(res);
            setFormError(null);
            setReadOnlyFormSwitch(res);
          } else {
            // api call with ALL
            const readonly = true;
            getInProgressFajitaFormById(id, agency as string, readonly);
          }
        } else {
          setFormError(defaults.errorForm);
        }
      })
      .catch((e: any) => console.error('e', e));
  };
  const getFormUI = () => {
    if (formError) {
      return <div>{formError}</div>;
    }
    const isFacetoFace = savedForm && isF2Fform((savedForm as any)?.formkey);
    if (savedForm) {
      return (
        <FormDiv id="fajita-from">
          <FajitaHeaderDiv>
            <FajitaFormHeader
              formData={savedForm as any}
              style={{ width: '100%' }}
            />
          </FajitaHeaderDiv>
          <ReadOnlyForm
            formData={savedForm.formsubmission as any}
            pnNotes={formNotes}
            formaddons={savedForm.formaddons}
          />
          {isFacetoFace && <FajitaSignature formData={savedForm as any} />}
        </FormDiv>
      );
    }

    if (fajitaForm) {
      // todo type PatientFormProps
      return (
        <FormDiv id="fajita-from">
          <FajitaHeaderDiv>
            <FajitaFormHeader
              formData={fajitaForm as any}
              style={{ width: '100%' }}
            />
          </FajitaHeaderDiv>
          <FormRenderer formData={fajitaForm as any} />
        </FormDiv>
      );
    }
    return (
      <FormDiv>
        {defaults.loadingForm}
        <LinearProgress />
      </FormDiv>
    );
  };

  return (
    <FormEngineContextProvider>
      <div>
        <APIAlert
          errorInfo={errorInfo}
          persistentError
          handleClose={() => clearError()}
        />
        {getFormUI()}
      </div>
    </FormEngineContextProvider>
  );
};
